import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UpgradeDialogSource } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";

import AboutFormLinks from "features/Auth/Onboarding/AboutForm/Links";
import { SocialMediaLinks } from "features/Auth/Onboarding/AboutForm/helpers";
import { TemplateContentType } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { hasSocialMediaProfile } from "utils/influencer";
import { GAMIFICATION_UNLOCKED_ALL_FEATURES } from "utils/localStorage";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";

import { getActiveStepForLevelOneFree, hasTaskByType } from "../helpers";
import { OrganizationGamificationTask } from "../schema";
import OnboardingCard from "./OnboardingCard";
import styles from "./styles";

const tasks = [
  {
    icon: "fa-regular fa-link",
    label: "Add portfolio link",
    taskName: OrganizationGamificationTask.add_portfolio,
  },
  {
    icon: "fa-regular fa-file-pen",
    label: "Create template",
    taskName: OrganizationGamificationTask.add_template,
  },
  {
    icon: "fa-regular fa-clock-rotate-left",
    label: "Create followup",
    taskName: OrganizationGamificationTask.add_followup,
  },
  {
    icon: "fa-regular fa-paper-plane",
    label: "Send 10 pitches",
    isPitches: true,
    taskName: OrganizationGamificationTask.send_pitch,
  },
];

interface Props {
  pitchCount: number | null;
  onRefreshPitchCount: () => void;
  fetchPitchCountLoading?: boolean;
  refreshLoading?: boolean;
  setShowPremiumFeatureUnlocked: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExperienceFreeLevelOne = ({
  pitchCount,
  onRefreshPitchCount,
  fetchPitchCountLoading,
  refreshLoading,
  setShowPremiumFeatureUnlocked,
}: Props) => {
  const navigate = useNavigate();
  const {
    gamificationProfile,
    currentOrg,
    profile,
    setProfile,
    setGamificationProfile,
  } = useContext(OrganizationUserContext);
  const { setCreateTemplateDialogOpen, setCurrentTemplateContentType } =
    useContext(OutreachTemplatesContext);
  const { setAlert } = useContext(AlertContext);
  const { plans, setUpgradeDialogSource } = useContext(SubscriptionContext);
  const { getToken } = useAuth();
  const proPrice = plans ? plans[Plans.PRO]?.price : null;
  const proEmailCredits = plans ? plans[Plans.PRO]?.monthly_limit : null;

  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [linksValidation, setLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [links, setLinks] = useState<SocialMediaLinks[]>([]);
  const [saving, setSaving] = useState(false);
  const [templateRefreshLoading, setTemplateRefreshLoading] = useState(false);
  const [indexOfCompletedTask, setIndexOfCompletedTask] = useState<number>(0);
  const { completeTask, refreshGamificationProfile } = useGamification();

  const openPortfolioDialog = () => {
    trackEvent("Gamification - Add Portfolio");
    if (profile) setOnboardingForm(profile);
    setLinks([]);
    setPortfolioDialogOpen(true);
  };

  const handleSavePortfolio = async () => {
    setSaving(true);
    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        { ...onboardingForm, page: "Gamification" },
      );
      setProfile(organizationProfile);
      setAlert("Portfolio links saved", "success");
      setPortfolioDialogOpen(false);
      completeTask(OrganizationGamificationTask.add_portfolio);
    } catch (error) {
      setAlert(error, "error");
    } finally {
      setSaving(false);
    }
  };

  const openTemplateDialog = (contentType: TemplateContentType) => {
    trackEvent("Gamification - Create Template", {
      contentType,
    });
    setCreateTemplateDialogOpen(true);
    setCurrentTemplateContentType(contentType);
  };

  const openCampaignDialog = () => {
    trackEvent("Gamification - Create Campaign");
    navigate(`new`);
  };

  const refreshTemplateTask = async (
    taskType: OrganizationGamificationTask,
  ) => {
    try {
      setTemplateRefreshLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/refresh-template-task`,
        "POST",
        {},
        { taskType },
      );
      await refreshGamificationProfile();
    } catch (error) {
      setAlert(error, "error");
    } finally {
      setTemplateRefreshLoading(false);
    }
  };

  const percentage = pitchCount ? Math.round((pitchCount / 10) * 100) : 0;
  const completed = indexOfCompletedTask >= 4;

  useEffect(() => {
    if (gamificationProfile) {
      const index = getActiveStepForLevelOneFree(gamificationProfile);
      setIndexOfCompletedTask(index);
      if (index >= 4) {
        const hasSeenDialog =
          localStorage.getItem(
            `${currentOrg?.id}-${GAMIFICATION_UNLOCKED_ALL_FEATURES}`,
          ) === "true";
        if (!hasSeenDialog) {
          setShowPremiumFeatureUnlocked(true);
        }

        setGamificationProfile((prev) =>
          prev
            ? {
                ...prev,
                unlockPremiumFeature: true,
              }
            : prev,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamificationProfile?.tasksCompleted?.length]);

  if (completed) {
    return (
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: "#eefff4",
          border: "1.5px solid #00C950",
          borderRadius: "12px",
          maxWidth: "1000px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "#009966",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box
              component="i"
              className="fa-solid fa-lock-open"
              sx={{ fontSize: 16, color: "white" }}
            />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize="1rem" color="#014339">
              Premium Access Unlocked
            </Typography>
            <Typography fontSize="0.875rem">
              Access to direct contacts + 500k brands
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            background: "white",
            border: "1.5px solid #E5E7EB",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }} gutterBottom>
              Want more credits?
            </Typography>
            <Typography>
              Get{" "}
              <Box sx={{ color: "purple.dark" }} component="span">
                <strong>{proEmailCredits} emails / month</strong>
              </Box>{" "}
              + unlimited features
            </Typography>
          </Box>
          <Button
            variant="contained"
            disableElevation
            size="small"
            onClick={() =>
              setUpgradeDialogSource(
                UpgradeDialogSource.gamification_more_emails,
              )
            }
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Upgrade
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={styles.unlockDirectContacts}>
        <Grid container gap={1.5}>
          <Box sx={styles.crownCircle}>
            <Box
              component="i"
              className="fa-solid fa-crown"
              sx={styles.crownIcon}
            />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize="1rem">
              Unlock Direct Contacts &amp; Premium Features
            </Typography>
            <Typography>{indexOfCompletedTask}/4 tasks completed</Typography>
          </Box>
        </Grid>

        <LinearProgress
          variant="determinate"
          value={(indexOfCompletedTask / 4) * 100}
          sx={styles.headerProgressBar}
        />

        <Box sx={styles.taskListWrap}>
          {tasks.map(({ icon, label, isPitches, taskName }) => {
            const isCompleted =
              gamificationProfile?.tasksCompleted?.includes(taskName);
            return (
              <Box key={label} sx={styles.taskCard(isCompleted)}>
                <Box sx={styles.taskCardHeader}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {gamificationProfile?.tasksCompleted?.includes(taskName) ? (
                      <Box
                        component="i"
                        sx={{ color: "green.main", mr: 0.5 }}
                        className="fa-solid fa-circle-check"
                      />
                    ) : (
                      <Box
                        component="i"
                        sx={{ color: grey[400], mr: 0.5 }}
                        className="fa-regular fa-circle"
                      />
                    )}
                    <Typography fontWeight={500} fontSize="0.875rem">
                      {label}
                    </Typography>
                  </Box>
                  {!isCompleted && (
                    <Box
                      component="i"
                      className={icon}
                      sx={styles.taskCardIcon}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={styles.unlockFeaturesBox}>
          <Box sx={styles.unlockLockIconRow}>
            <Box
              component="i"
              className="fa-regular fa-lock"
              sx={styles.unlockLockIcon}
            />
            <Typography
              fontWeight={600}
              fontSize="0.75rem"
              color="#101828"
              lineHeight="16px"
            >
              Unlock:
            </Typography>
          </Box>
          <Box sx={styles.unlockFeaturesList}>
            {[
              "Direct Contacts",
              "500K Brand Contacts",
              "Advanced Features",
            ].map((item) => (
              <Typography
                key={item}
                fontWeight={700}
                fontSize="0.75rem"
                color="#364153"
                lineHeight="16px"
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>

        {proPrice && (
          <Typography
            textAlign={"center"}
            fontSize="0.75rem"
            color="#6B7280"
            mt={2}
            mb={0.5}
          >
            or skip by{" "}
            <Box
              component="a"
              onClick={() =>
                setUpgradeDialogSource(UpgradeDialogSource.gamification_bypass)
              }
              sx={styles.proLink}
            >
              upgrading
            </Box>
          </Typography>
        )}
      </Box>

      {/* Onboarding Steps card */}
      {indexOfCompletedTask < 4 && (
        <Box sx={{ mt: 2 }}>
          <Typography fontWeight={700} fontSize="1rem" gutterBottom>
            Onboarding Steps
          </Typography>
          <OnboardingCard
            icon="fa-regular fa-link"
            title="Add Your Portfolio Link"
            time="<1"
            description="Share your Instagram, UGC Portfolio, TikTok, YouTube, Podcast URL, or relevant website"
            explanation="Brands need to see what you have to offer before they respond. Having your portfolio or sample of work ready makes you look professional and prepared."
            buttonTitle="Add Your Portfolio Link"
            onClick={openPortfolioDialog}
            completed={hasTaskByType(
              gamificationProfile,
              OrganizationGamificationTask.add_portfolio,
            )}
          />

          <OnboardingCard
            icon="fa-regular fa-file-lines"
            title="Create Your Pitch Template"
            time="5-10"
            description="Write a personalized pitch you can customize for each brand"
            explanation="A solid template saves you time and ensures you never forget key details. You'll customize it for each brand, but having a foundation makes pitching 10x faster."
            buttonTitle="Create Pitch Template"
            onClick={() => openTemplateDialog(TemplateContentType.OUTREACH)}
            completed={hasTaskByType(
              gamificationProfile,
              OrganizationGamificationTask.add_template,
            )}
            locked={indexOfCompletedTask < 1}
            onRefresh={() =>
              refreshTemplateTask(OrganizationGamificationTask.add_template)
            }
            refreshLoading={templateRefreshLoading}
          />
          <OnboardingCard
            icon="fa-regular fa-file-lines"
            title="Create Follow-Up Template"
            time="2-3"
            description="Write a personalized follow-up pitch you can customize for each brand"
            explanation="A solid follow-up template helps you maintain momentum and increase your chances of getting a response."
            buttonTitle="Create Follow-Up Template"
            onClick={() => openTemplateDialog(TemplateContentType.FOLLOWUP)}
            completed={hasTaskByType(
              gamificationProfile,
              OrganizationGamificationTask.add_followup,
            )}
            locked={indexOfCompletedTask < 2}
            onRefresh={() =>
              refreshTemplateTask(OrganizationGamificationTask.add_followup)
            }
            refreshLoading={templateRefreshLoading}
          />
          <OnboardingCard
            icon="fa-regular fa-paper-plane"
            title="Send 10 Brand Pitch"
            time="1-2"
            description="Start reaching out to brands that align with your content"
            explanation="Most creators quit after 2-3 pitches. 10+ pitches is a great start to get yourself in the habit of pitching consistently. Volume is key to getting deals."
            buttonTitle="Send"
            onClick={openCampaignDialog}
            completed={hasTaskByType(
              gamificationProfile,
              OrganizationGamificationTask.send_pitch,
            )}
            locked={indexOfCompletedTask < 3}
            body={
              <Box sx={{ my: 2 }}>
                {fetchPitchCountLoading ? (
                  <>
                    <Grid
                      container
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Skeleton width={80} height={20} />
                      <Skeleton width={110} height={20} />
                    </Grid>
                    <Skeleton variant="rounded" height={8} />
                  </>
                ) : (
                  <>
                    <Grid
                      container
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Progress
                        <IconButton
                          size="small"
                          onClick={onRefreshPitchCount}
                          disabled={refreshLoading}
                        >
                          {refreshLoading ? (
                            <CircularProgress size={12} />
                          ) : (
                            <i
                              className="fa-regular fa-rotate-right"
                              style={{ fontSize: 12 }}
                            />
                          )}
                        </IconButton>
                      </Box>
                      {(pitchCount || 0) < 10 ? (
                        <Typography
                          sx={{ color: "purple.dark", fontWeight: 700 }}
                        >
                          {pitchCount || 0} / 10 emails sent
                        </Typography>
                      ) : (
                        <Typography
                          sx={{ color: "green.main", fontWeight: 700 }}
                        >
                          Completed
                        </Typography>
                      )}
                    </Grid>
                    <LinearProgress
                      variant="determinate"
                      value={(pitchCount || 0) < 10 ? percentage : 100}
                      sx={styles.linearProgress}
                    />
                  </>
                )}
              </Box>
            }
          />
        </Box>
      )}

      <Dialog
        open={portfolioDialogOpen}
        onClose={() => setPortfolioDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Portfolio Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <AboutFormLinks
              onboardingForm={onboardingForm}
              setOnboardingForm={setOnboardingForm}
              setLinksValidation={setLinksValidation}
              links={links}
              setLinks={setLinks}
              validation={linksValidation}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortfolioDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            loading={saving}
            variant="contained"
            onClick={handleSavePortfolio}
            disableElevation
            disabled={!hasSocialMediaProfile(onboardingForm)}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExperienceFreeLevelOne;
